import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Box,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  Paper,
  Button,
  Select,
  FormControl,
  InputLabel,
  ListSubheader,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
  Badge,
  TableSortLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Refresh, FilterList, Close, Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, startOfDay } from 'date-fns';
import { enUS, ro } from 'date-fns/locale';
import { shiftsApi } from '../services/api';

const locales = {
  en: enUS,
  ro: ro
};

const dateFormats = {
  en: 'MM/dd/yyyy',
  ro: 'dd.MM.yyyy'
};

const timeFormats = {
  en: 'MM/dd/yyyy h:mm a',
  ro: 'dd.MM.yyyy HH:mm'
};

const ShiftTable = ({
  shifts,
  loading,
  page,
  totalElements,
  onPageChange,
  filters,
  onFilterChange,
  autoRefresh,
  onAutoRefreshChange,
  onRefresh,
  showTruckEnter = true,
  showVanEnter = true,
}) => {
  const { t, i18n } = useTranslation();
  const currentLocale = locales[i18n.language] || locales.ro;
  const dateFormat = dateFormats[i18n.language] || dateFormats.ro;
  const timeFormat = timeFormats[i18n.language] || timeFormats.ro;
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);

  // Sorting state
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('');

  // Filter options
  const [personOptions, setPersonOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [checkInLocationOptions, setCheckInLocationOptions] = useState([]);
  const [checkOutLocationOptions, setCheckOutLocationOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Filter menu anchor states
  const [personAnchorEl, setPersonAnchorEl] = useState(null);
  const [companyAnchorEl, setCompanyAnchorEl] = useState(null);
  const [checkInLocationAnchorEl, setCheckInLocationAnchorEl] = useState(null);
  const [checkOutLocationAnchorEl, setCheckOutLocationAnchorEl] = useState(null);

  // Local state for multi-select filters
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedCheckInLocations, setSelectedCheckInLocations] = useState([]);
  const [selectedCheckOutLocations, setSelectedCheckOutLocations] = useState([]);

  // Search terms for filter menus - moved from renderFilterMenu to component level
  const [personSearchTerm, setPersonSearchTerm] = useState('');
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [checkInLocationSearchTerm, setCheckInLocationSearchTerm] = useState('');
  const [checkOutLocationSearchTerm, setCheckOutLocationSearchTerm] = useState('');

  // Initialize local state from filters
  useEffect(() => {
    console.log('Initializing filters:', filters);
    if (filters.personFilter) {
      setSelectedPersons(filters.personFilter.split(',').filter(Boolean));
    } else {
      setSelectedPersons([]);
    }

    if (filters.companyFilter) {
      setSelectedCompanies(filters.companyFilter.split(',').filter(Boolean));
    } else {
      setSelectedCompanies([]);
    }

    if (filters.checkInLocationFilter) {
      setSelectedCheckInLocations(filters.checkInLocationFilter.split(',').filter(Boolean));
    } else {
      setSelectedCheckInLocations([]);
    }

    if (filters.checkOutLocationFilter) {
      setSelectedCheckOutLocations(filters.checkOutLocationFilter.split(',').filter(Boolean));
    } else {
      setSelectedCheckOutLocations([]);
    }
  }, [filters]);

  // Fetch filter options when date range changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!filters.startDate || !filters.endDate) return;

      try {
        setLoadingOptions(true);
        const response = await shiftsApi.getFilterOptions(
          filters.startDate,
          filters.endDate
        );

        if (response.data) {
          setPersonOptions(response.data.persons || []);
          setCompanyOptions(response.data.companies || []);
          setCheckInLocationOptions(response.data.checkInLocations || []);
          setCheckOutLocationOptions(response.data.checkOutLocations || []);
        }
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchFilterOptions();
  }, [filters.startDate, filters.endDate]);

  // Only sync with filters if they have sorting values
  useEffect(() => {
    if (filters.sortBy) {
      // Sync local state with filters if they already have sorting
      setOrderBy(filters.sortBy);
      setOrder(filters.sortDirection);
    }
  }, [filters.sortBy, filters.sortDirection]);

  const shouldHighlightRow = (shift) => {
    return !shift.valid;
  };

  const handleStartDateChange = (date) => {
    setStartDateError(null);
    setEndDateError(null);
    
    const endDate = filters.endDate ? new Date(filters.endDate) : new Date();
    if (date && startOfDay(date) > startOfDay(endDate)) {
      setStartDateError(t('shifts.errors.startDateAfterEnd'));
    }
    onFilterChange({
      ...filters,
      startDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handleEndDateChange = (date) => {
    setStartDateError(null);
    setEndDateError(null);
    
    const startDate = filters.startDate ? new Date(filters.startDate) : new Date();
    if (date && startOfDay(date) < startOfDay(startDate)) {
      setEndDateError(t('shifts.errors.endDateBeforeStart'));
    }
    onFilterChange({
      ...filters,
      endDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handlePageChange = (event, newPage) => {
    onPageChange(newPage);
  };

  // Column filter handlers for multi-select
  const handlePersonFilterChange = (person) => {
    const newSelectedPersons = selectedPersons.includes(person)
      ? selectedPersons.filter(p => p !== person)
      : [...selectedPersons, person];

    setSelectedPersons(newSelectedPersons);
  };

  const handleCompanyFilterChange = (company) => {
    const newSelectedCompanies = selectedCompanies.includes(company)
      ? selectedCompanies.filter(c => c !== company)
      : [...selectedCompanies, company];

    setSelectedCompanies(newSelectedCompanies);
  };

  const handleCheckInLocationFilterChange = (location) => {
    const newSelectedLocations = selectedCheckInLocations.includes(location)
      ? selectedCheckInLocations.filter(l => l !== location)
      : [...selectedCheckInLocations, location];

    setSelectedCheckInLocations(newSelectedLocations);
  };

  const handleCheckOutLocationFilterChange = (location) => {
    const newSelectedLocations = selectedCheckOutLocations.includes(location)
      ? selectedCheckOutLocations.filter(l => l !== location)
      : [...selectedCheckOutLocations, location];

    setSelectedCheckOutLocations(newSelectedLocations);
  };

  // Apply filters
  const applyPersonFilter = () => {
    const filteredPersons = selectedPersons.filter(Boolean);
    console.log('Applying person filter:', filteredPersons);

    // Only update if there are changes
    const currentFilter = filters.personFilter ? filters.personFilter.split(',').filter(Boolean) : [];
    const hasChanges = filteredPersons.length !== currentFilter.length ||
      filteredPersons.some(p => !currentFilter.includes(p));

    if (hasChanges) {
      onFilterChange({
        ...filters,
        personFilter: filteredPersons.join(',')
      });
    }

    setPersonAnchorEl(null);
    setPersonSearchTerm('');
  };

  const applyCompanyFilter = () => {
    const filteredCompanies = selectedCompanies.filter(Boolean);
    console.log('Applying company filter:', filteredCompanies);

    // Only update if there are changes
    const currentFilter = filters.companyFilter ? filters.companyFilter.split(',').filter(Boolean) : [];
    const hasChanges = filteredCompanies.length !== currentFilter.length ||
      filteredCompanies.some(c => !currentFilter.includes(c));

    if (hasChanges) {
      onFilterChange({
        ...filters,
        companyFilter: filteredCompanies.join(',')
      });
    }

    setCompanyAnchorEl(null);
    setCompanySearchTerm('');
  };

  const applyCheckInLocationFilter = () => {
    const filteredLocations = selectedCheckInLocations.filter(Boolean);
    console.log('Applying check-in location filter:', filteredLocations);

    // Only update if there are changes
    const currentFilter = filters.checkInLocationFilter ? filters.checkInLocationFilter.split(',').filter(Boolean) : [];
    const hasChanges = filteredLocations.length !== currentFilter.length ||
      filteredLocations.some(l => !currentFilter.includes(l));

    if (hasChanges) {
      onFilterChange({
        ...filters,
        checkInLocationFilter: filteredLocations.join(',')
      });
    }

    setCheckInLocationAnchorEl(null);
    setCheckInLocationSearchTerm('');
  };

  const applyCheckOutLocationFilter = () => {
    const filteredLocations = selectedCheckOutLocations.filter(Boolean);
    console.log('Applying check-out location filter:', filteredLocations);

    // Only update if there are changes
    const currentFilter = filters.checkOutLocationFilter ? filters.checkOutLocationFilter.split(',').filter(Boolean) : [];
    const hasChanges = filteredLocations.length !== currentFilter.length ||
      filteredLocations.some(l => !currentFilter.includes(l));

    if (hasChanges) {
      onFilterChange({
        ...filters,
        checkOutLocationFilter: filteredLocations.join(',')
      });
    }

    setCheckOutLocationAnchorEl(null);
    setCheckOutLocationSearchTerm('');
  };

  // Clear filters
  const clearPersonFilter = () => {
    setSelectedPersons([]);
    onFilterChange({ ...filters, personFilter: '' });
    setPersonAnchorEl(null);
    setPersonSearchTerm('');
  };

  const clearCompanyFilter = () => {
    setSelectedCompanies([]);
    onFilterChange({ ...filters, companyFilter: '' });
    setCompanyAnchorEl(null);
    setCompanySearchTerm('');
  };

  const clearCheckInLocationFilter = () => {
    setSelectedCheckInLocations([]);
    onFilterChange({ ...filters, checkInLocationFilter: '' });
    setCheckInLocationAnchorEl(null);
    setCheckInLocationSearchTerm('');
  };

  const clearCheckOutLocationFilter = () => {
    setSelectedCheckOutLocations([]);
    onFilterChange({ ...filters, checkOutLocationFilter: '' });
    setCheckOutLocationAnchorEl(null);
    setCheckOutLocationSearchTerm('');
  };

  const formatDateTime = (date) => {
    if (!date || date === '-') return '-';
    try {
      // Parse the date string in Romanian format (dd.MM.yyyy HH:mm)
      const [datePart, timePart] = date.split(' ');
      const [day, month, year] = datePart.split('.');
      const [hours, minutes] = timePart.split(':');
      
      // Create date in local timezone
      const dateObj = new Date(year, month - 1, day, hours, minutes);
      
      if (isNaN(dateObj.getTime())) return '-';
      
      return dateObj.toLocaleString(i18n.language, { 
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: i18n.language === 'en' // Use 12-hour format for English, 24-hour for Romanian
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  const translateLocation = (location) => {
    if (!location || location === '-') return '-';
    return t(`shifts.locations.${location}`);
  };

  // Helper function to get the appropriate search term and setter based on anchor element
  const getSearchTermAndSetter = (anchorEl) => {
    if (anchorEl === personAnchorEl) {
      return [personSearchTerm, setPersonSearchTerm];
    } else if (anchorEl === companyAnchorEl) {
      return [companySearchTerm, setCompanySearchTerm];
    } else if (anchorEl === checkInLocationAnchorEl) {
      return [checkInLocationSearchTerm, setCheckInLocationSearchTerm];
    } else if (anchorEl === checkOutLocationAnchorEl) {
      return [checkOutLocationSearchTerm, setCheckOutLocationSearchTerm];
    }
    return ['', () => {}]; // Default fallback
  };

  // Filter menu components with checkboxes
  const renderFilterMenu = (
    anchorEl,
    setAnchorEl,
    options,
    selectedValues,
    handleFilterChange,
    applyFilter,
    clearFilter,
    title,
    translationPrefix = null
  ) => {
    // Use the appropriate search term and setter based on the anchor element
    const [searchTerm, setSearchTerm] = getSearchTermAndSetter(anchorEl);

    // Ensure we have valid options to display
    const validOptions = options.filter(option => option !== null && option !== undefined && option !== '');

    // Filter options based on search term
    const filteredOptions = validOptions.filter(option => {
      const displayText = translationPrefix ? t(`${translationPrefix}.${option}`) : option;
      return displayText.toLowerCase().includes((searchTerm || '').toLowerCase());
    });

    // Select all filtered options
    const handleSelectAll = () => {
      const newSelected = [...selectedValues];
      filteredOptions.forEach(option => {
        if (!newSelected.includes(option)) {
          newSelected.push(option);
        }
      });
      if (translationPrefix === 'shifts.locations') {
        if (anchorEl === checkInLocationAnchorEl) {
          setSelectedCheckInLocations(newSelected);
        } else if (anchorEl === checkOutLocationAnchorEl) {
          setSelectedCheckOutLocations(newSelected);
        }
      } else if (anchorEl === personAnchorEl) {
        setSelectedPersons(newSelected);
      } else if (anchorEl === companyAnchorEl) {
        setSelectedCompanies(newSelected);
      }
    };

    // Deselect all filtered options
    const handleDeselectAll = () => {
      let newSelected = [...selectedValues];
      filteredOptions.forEach(option => {
        newSelected = newSelected.filter(item => item !== option);
      });
      if (translationPrefix === 'shifts.locations') {
        if (anchorEl === checkInLocationAnchorEl) {
          setSelectedCheckInLocations(newSelected);
        } else if (anchorEl === checkOutLocationAnchorEl) {
          setSelectedCheckOutLocations(newSelected);
        }
      } else if (anchorEl === personAnchorEl) {
        setSelectedPersons(newSelected);
      } else if (anchorEl === companyAnchorEl) {
        setSelectedCompanies(newSelected);
      }
    };

    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setSearchTerm('');
          setAnchorEl(null);
        }}
        PaperProps={{
          style: {
            maxHeight: 800,
            width: 280,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
              {selectedValues.length > 0 && (
                <Button size="small" onClick={clearFilter} color="primary">
                  {t('common.clear')}
                </Button>
              )}
            </Box>

            <Divider sx={{ mb: 1 }} />

            {validOptions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {selectedValues.length} / {validOptions.length} {t('common.selected', { defaultValue: 'selected' })}
              </Typography>
            )}

            {validOptions.length > 5 && (
              <TextField
                size="small"
                placeholder={anchorEl === companyAnchorEl ? t('shifts.searchCompany') : t('common.search')}
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                variant="outlined"
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: searchTerm ? (
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null
                }}
              />
            )}

            {filteredOptions.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  color="primary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {t('common.selectAll')}
                </Button>
                <Button
                  size="small"
                  onClick={handleDeselectAll}
                  color="primary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {t('common.deselectAll')}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            mb: 2,
            maxHeight: 250,
          }}>
            {loadingOptions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredOptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                {searchTerm ? t('common.noSearchResults') : t('shifts.noFilterOptions')}
              </Typography>
            ) : (
              filteredOptions.map((option) => (
                <MenuItem
                  key={option}
                  dense
                  onClick={() => handleFilterChange(option)}
                  sx={{ py: 0.5 }}
                >
                  <Checkbox
                    checked={selectedValues.includes(option)}
                    size="small"
                    color="primary"
                  />
                  <ListItemText
                    primary={translationPrefix ? t(`${translationPrefix}.${option}`) : option}
                  />
                </MenuItem>
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('');
                setAnchorEl(null);
              }}
              color="inherit"
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={applyFilter}
              color="primary"
              disabled={loadingOptions}
            >
              {t('common.apply')}
            </Button>
          </Box>
        </Box>
      </Menu>
    );
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    // If clicking on the same column that's already sorted, cycle through: asc -> desc -> no sort
    if (orderBy === property) {
      if (order === 'asc') {
        setOrder('desc');
        setOrderBy(property);
        onFilterChange({
          ...filters,
          sortBy: property,
          sortDirection: 'desc'
        });
      } else {
        // Clear sorting
        setOrder('');
        setOrderBy('');
        onFilterChange({
          ...filters,
          sortBy: '',
          sortDirection: ''
        });
      }
    } else {
      // New column selected, start with ascending
      setOrder('asc');
      setOrderBy(property);
      onFilterChange({
        ...filters,
        sortBy: property,
        sortDirection: 'asc'
      });
    }
  };

  // Clear sorting function
  const handleClearSort = () => {
    setOrder('');
    setOrderBy('');
    onFilterChange({
      ...filters,
      sortBy: '',
      sortDirection: ''
    });
  };

  // Clear all filters function
  const handleClearAllFilters = () => {
    // Reset all local filter states
    setSelectedPersons([]);
    setSelectedCompanies([]);
    setSelectedCheckInLocations([]);
    setSelectedCheckOutLocations([]);
    
    // Clear all filter search terms
    setPersonSearchTerm('');
    setCompanySearchTerm('');
    setCheckInLocationSearchTerm('');
    setCheckOutLocationSearchTerm('');
    
    // Update the filters object, keeping only date range and sorting
    onFilterChange({
      startDate: filters.startDate,
      endDate: filters.endDate,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      personFilter: '',
      companyFilter: '',
      checkInLocationFilter: '',
      checkOutLocationFilter: '',
      activeShifts: false
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
            <DatePicker
              label={t('shifts.startDate')}
              value={filters.startDate ? new Date(filters.startDate) : new Date()}
              onChange={handleStartDateChange}
              slotProps={{ 
                textField: { 
                  size: 'small',
                  error: !!startDateError,
                  helperText: startDateError,
                  disabled: filters.activeShifts
                } 
              }}
              format={dateFormat}
              views={['year', 'month', 'day']}
              disabled={filters.activeShifts}
            />
            <DatePicker
              label={t('shifts.endDate')}
              value={filters.endDate ? new Date(filters.endDate) : new Date()}
              onChange={handleEndDateChange}
              slotProps={{ 
                textField: { 
                  size: 'small',
                  error: !!endDateError,
                  helperText: endDateError,
                  disabled: filters.activeShifts
                } 
              }}
              format={dateFormat}
              views={['year', 'month', 'day']}
              disabled={filters.activeShifts}
            />
          </LocalizationProvider>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.activeShifts}
                onChange={(e) => {
                  onFilterChange({
                    ...filters,
                    activeShifts: e.target.checked
                  });
                }}
              />
            }
            label={t('shifts.activeShifts', 'Active Shifts')}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {orderBy && (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={handleClearSort}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              {t('common.clearSort')}
            </Button>
          )}
          {(filters.personFilter || filters.companyFilter || filters.checkInLocationFilter || filters.checkOutLocationFilter) && (
            <Button
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearAllFilters}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              {t('common.clearFilters')}
            </Button>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => onAutoRefreshChange(e.target.checked)}
              />
            }
            label={t('shifts.autoRefresh')}
          />
          <IconButton 
            onClick={onRefresh} 
            disabled={loading}
            aria-label={t('shifts.refresh')}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: '600px', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : shifts.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <TableContainer sx={{ 
            flexGrow: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            position: 'relative'
          }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ 
                  '& th': { 
                    bgcolor: (theme) => theme.palette.mode === 'light' 
                      ? theme.palette.grey[100]
                      : theme.palette.grey[900],
                    fontWeight: 600,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    padding: '12px 16px',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  } 
                }}>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'employeeName'}
                      direction={orderBy === 'employeeName' ? order : 'asc'}
                      onClick={() => handleRequestSort('employeeName')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('shifts.person')}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            setPersonAnchorEl(e.currentTarget);
                          }}
                          color={selectedPersons.length > 0 ? "primary" : "default"}
                        >
                          {selectedPersons.length > 0 ? (
                            <Badge badgeContent={selectedPersons.length} color="primary">
                              <FilterList fontSize="small" />
                            </Badge>
                          ) : (
                            <FilterList fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableSortLabel>
                    {renderFilterMenu(
                      personAnchorEl,
                      setPersonAnchorEl,
                      personOptions,
                      selectedPersons,
                      handlePersonFilterChange,
                      applyPersonFilter,
                      clearPersonFilter,
                      t('shifts.filterPerson')
                    )}
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'company'}
                      direction={orderBy === 'company' ? order : 'asc'}
                      onClick={() => handleRequestSort('company')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('shifts.company')}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            setCompanyAnchorEl(e.currentTarget);
                          }}
                          color={selectedCompanies.length > 0 ? "primary" : "default"}
                        >
                          {selectedCompanies.length > 0 ? (
                            <Badge badgeContent={selectedCompanies.length} color="primary">
                              <FilterList fontSize="small" />
                            </Badge>
                          ) : (
                            <FilterList fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableSortLabel>
                    {renderFilterMenu(
                      companyAnchorEl,
                      setCompanyAnchorEl,
                      companyOptions,
                      selectedCompanies,
                      handleCompanyFilterChange,
                      applyCompanyFilter,
                      clearCompanyFilter,
                      t('shifts.filterCompany')
                    )}
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'checkInLocation'}
                      direction={orderBy === 'checkInLocation' ? order : 'asc'}
                      onClick={() => handleRequestSort('checkInLocation')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('shifts.checkInLocation')}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            setCheckInLocationAnchorEl(e.currentTarget);
                          }}
                          color={selectedCheckInLocations.length > 0 ? "primary" : "default"}
                        >
                          {selectedCheckInLocations.length > 0 ? (
                            <Badge badgeContent={selectedCheckInLocations.length} color="primary">
                              <FilterList fontSize="small" />
                            </Badge>
                          ) : (
                            <FilterList fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableSortLabel>
                    {renderFilterMenu(
                      checkInLocationAnchorEl,
                      setCheckInLocationAnchorEl,
                      checkInLocationOptions,
                      selectedCheckInLocations,
                      handleCheckInLocationFilterChange,
                      applyCheckInLocationFilter,
                      clearCheckInLocationFilter,
                      t('shifts.filterLocation'),
                      'shifts.locations'
                    )}
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'checkOutLocation'}
                      direction={orderBy === 'checkOutLocation' ? order : 'asc'}
                      onClick={() => handleRequestSort('checkOutLocation')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('shifts.checkOutLocation')}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            setCheckOutLocationAnchorEl(e.currentTarget);
                          }}
                          color={selectedCheckOutLocations.length > 0 ? "primary" : "default"}
                        >
                          {selectedCheckOutLocations.length > 0 ? (
                            <Badge badgeContent={selectedCheckOutLocations.length} color="primary">
                              <FilterList fontSize="small" />
                            </Badge>
                          ) : (
                            <FilterList fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableSortLabel>
                    {renderFilterMenu(
                      checkOutLocationAnchorEl,
                      setCheckOutLocationAnchorEl,
                      checkOutLocationOptions,
                      selectedCheckOutLocations,
                      handleCheckOutLocationFilterChange,
                      applyCheckOutLocationFilter,
                      clearCheckOutLocationFilter,
                      t('shifts.filterLocation'),
                      'shifts.locations'
                    )}
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'clockInTime'}
                      direction={orderBy === 'clockInTime' ? order : 'asc'}
                      onClick={() => handleRequestSort('clockInTime')}
                    >
                      {t('shifts.clockInTime')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'clockOutTime'}
                      direction={orderBy === 'clockOutTime' ? order : 'asc'}
                      onClick={() => handleRequestSort('clockOutTime')}
                    >
                      {t('shifts.clockOutTime')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'totalTimeWorked'}
                      direction={orderBy === 'totalTimeWorked' ? order : 'asc'}
                      onClick={() => handleRequestSort('totalTimeWorked')}
                    >
                      {t('shifts.totalTime')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'cardId'}
                      direction={orderBy === 'cardId' ? order : 'asc'}
                      onClick={() => handleRequestSort('cardId')}
                    >
                      {t('shifts.cardId')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map((shift) => {
                  const highlight = shouldHighlightRow(shift);
                  return (
                    <TableRow 
                      key={shift.id}
                      sx={{
                        '& td': {
                          color: highlight ? (theme) => 
                            theme.palette.mode === 'light' 
                              ? '#d32f2f !important'  // bright red for light theme
                              : '#ff8a80 !important'  // pale red for dark theme
                            : 'inherit',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: {
                            xs: '120px',
                            sm: '180px',
                            md: '250px'
                          },
                          padding: '12px 16px'
                        },
                        '&:hover': {
                          '& td': {
                            color: highlight ? (theme) => 
                              theme.palette.mode === 'light' 
                                ? '#d32f2f !important'  // bright red for light theme
                                : '#ff8a80 !important'  // pale red for dark theme
                              : 'inherit'
                          },
                          backgroundColor: (theme) => 
                            theme.palette.mode === 'light' 
                              ? 'rgba(0, 0, 0, 0.04)' 
                              : 'rgba(255, 255, 255, 0.08)'
                        }
                      }}
                    >
                      <TableCell>{shift.employeeName}</TableCell>
                      <TableCell>{shift.company}</TableCell>
                      <TableCell>{shift.checkInLocation === '-' ? '-' : t(`shifts.locations.${shift.checkInLocation}`)}</TableCell>
                      <TableCell>{shift.checkOutLocation === '-' ? '-' : t(`shifts.locations.${shift.checkOutLocation}`)}</TableCell>
                      <TableCell>{formatDateTime(shift.clockInTime)}</TableCell>
                      <TableCell>{formatDateTime(shift.clockOutTime)}</TableCell>
                      <TableCell>{shift.totalTimeWorked}</TableCell>
                      <TableCell>{shift.cardId}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => 
              t('shifts.paginationLabel', { from, to }) + ` (${t('shifts.total')}: ${count})`
            }
          />
        </Box>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '600px',
          backgroundColor: (theme) => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1
        }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            {t('shifts.noRecordsFound', 'No shift records found')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('shifts.tryAdjustingFilters', 'Try adjusting your filters or date range')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ShiftTable; 